import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { COLLECTIONS } from '../../constants/firebaseConfig';
import { PUBLIC_PRODUCTS_SUBSCRIPTION_PURPOSE } from '../publicProductsSubscriptionPayment';
import { parseWipayDataQueryParam } from './parseWipayCallback';
import { createLogger } from '../../utils/logger';
import {
  buildCartDocsByOrderId,
  buildPaymentRowForOrderId,
} from './listUnprocessedPayments';

const logger = createLogger('searchAdminPayments');

function normalizeSearchTerm(term) {
  return String(term || '').trim().toLowerCase();
}

function fieldMatches(value, normalizedTerm) {
  if (value == null || value === '') return false;
  return String(value).toLowerCase().includes(normalizedTerm);
}

function anyFieldMatches(normalizedTerm, ...values) {
  return values.some((value) => fieldMatches(value, normalizedTerm));
}

function isSubscriptionRecord(wipayData, checkoutData) {
  if (wipayData?.payment_kind === 'public_products_subscription') return true;
  return checkoutData?.payment_purpose === PUBLIC_PRODUCTS_SUBSCRIPTION_PURPOSE;
}

function collectMatchingUserIds(usersSnapshot, normalizedTerm) {
  const matchingUserIds = new Set();
  for (const userDoc of usersSnapshot.docs) {
    const userData = userDoc.data();
    const identityId = userData.identity_id;
    if (
      anyFieldMatches(
        normalizedTerm,
        userData.email,
        userData.first_name,
        userData.displayName,
        identityId,
        userDoc.id
      ) &&
      identityId
    ) {
      matchingUserIds.add(String(identityId));
    }
  }
  return matchingUserIds;
}

function userIdMatchesSearch(userId, matchingUserIds, normalizedTerm) {
  if (!userId) return false;
  if (matchingUserIds.has(String(userId))) return true;
  return fieldMatches(userId, normalizedTerm);
}

function collectOrderIdsFromRecords({
  normalizedTerm,
  rawTerm,
  matchingUserIds,
  wipaySnapshot,
  transactionsSnapshot,
  cartDocsByOrderId,
  directOrderIds = [],
}) {
  const orderIds = new Set(directOrderIds);

  for (const wipayDoc of wipaySnapshot.docs) {
    const data = wipayDoc.data();
    const checkoutData = parseWipayDataQueryParam(data.data);
    if (isSubscriptionRecord(data, checkoutData)) continue;

    const orderId = data.order_id || wipayDoc.id;
    if (
      anyFieldMatches(
        normalizedTerm,
        orderId,
        wipayDoc.id,
        data.order_id,
        data.transaction_id,
        data.user_id,
        data.customer_name,
        checkoutData.cartInstanceId,
        checkoutData.userId,
        checkoutData.customerName
      ) ||
      userIdMatchesSearch(data.user_id, matchingUserIds, normalizedTerm) ||
      userIdMatchesSearch(checkoutData.userId, matchingUserIds, normalizedTerm)
    ) {
      orderIds.add(orderId);
    }
  }

  for (const txDoc of transactionsSnapshot.docs) {
    const data = txDoc.data();
    if (
      anyFieldMatches(
        normalizedTerm,
        txDoc.id,
        data.cartInstanceId,
        data.paymentGatewayTransactionId,
        data.userId,
        data.customerName,
        data.cartDocId,
        data.supermarketName
      ) ||
      userIdMatchesSearch(data.userId, matchingUserIds, normalizedTerm)
    ) {
      orderIds.add(txDoc.id);
    }
  }

  for (const [orderId, { cartDoc, cartMatch }] of cartDocsByOrderId.entries()) {
    const checkoutSnapshot = cartMatch.cartInstance?.checkoutSnapshot || {};
    if (
      anyFieldMatches(
        normalizedTerm,
        orderId,
        cartMatch.cartInstanceId,
        cartDoc.id,
        cartMatch.cartInstance?.customerName,
        checkoutSnapshot.customerName,
        checkoutSnapshot.cartInstanceId,
        checkoutSnapshot.userId
      ) ||
      userIdMatchesSearch(cartDoc.id, matchingUserIds, normalizedTerm) ||
      userIdMatchesSearch(checkoutSnapshot.userId, matchingUserIds, normalizedTerm)
    ) {
      orderIds.add(orderId);
    }
  }

  return orderIds;
}

function rowMatchesSearchTerm(row, normalizedTerm, matchingUserIds) {
  return (
    anyFieldMatches(
      normalizedTerm,
      row.orderId,
      row.cartInstanceId,
      row.wipayTransactionId,
      row.customerName,
      row.customerEmail,
      row.customerUserId,
      row.cartDocId,
      row.supermarketName,
      row.message
    ) || userIdMatchesSearch(row.customerUserId, matchingUserIds, normalizedTerm)
  );
}

/**
 * Search cart payment records across WipayPayments, Transactions, Carts, and Users.
 * @param {string} searchTerm
 * @returns {Promise<Array<object>>}
 */
export async function searchAdminPayments(searchTerm) {
  const rawTerm = String(searchTerm || '').trim();
  const normalizedTerm = normalizeSearchTerm(rawTerm);
  if (!normalizedTerm) {
    return [];
  }

  try {
    const [wipaySnapshot, transactionsSnapshot, cartsSnapshot, usersSnapshot] =
      await Promise.all([
        getDocs(collection(db, COLLECTIONS.WIPAY_PAYMENTS)),
        getDocs(collection(db, COLLECTIONS.TRANSACTIONS)),
        getDocs(collection(db, COLLECTIONS.CARTS)),
        getDocs(collection(db, COLLECTIONS.USERS)),
      ]);

    const cartDocsByOrderId = buildCartDocsByOrderId(cartsSnapshot);
    const matchingUserIds = collectMatchingUserIds(usersSnapshot, normalizedTerm);

    const directOrderIds = [];
    const [wipayDirectSnap, transactionDirectSnap] = await Promise.all([
      getDoc(doc(db, COLLECTIONS.WIPAY_PAYMENTS, rawTerm)),
      getDoc(doc(db, COLLECTIONS.TRANSACTIONS, rawTerm)),
    ]);
    if (wipayDirectSnap.exists()) {
      directOrderIds.push(wipayDirectSnap.data()?.order_id || wipayDirectSnap.id);
    }
    if (transactionDirectSnap.exists()) {
      directOrderIds.push(transactionDirectSnap.id);
    }

    const orderIds = collectOrderIdsFromRecords({
      normalizedTerm,
      rawTerm,
      matchingUserIds,
      wipaySnapshot,
      transactionsSnapshot,
      cartDocsByOrderId,
      directOrderIds,
    });

    const rowsByOrderId = new Map();
    for (const orderId of orderIds) {
      const row = await buildPaymentRowForOrderId(orderId, cartDocsByOrderId);
      if (row && rowMatchesSearchTerm(row, normalizedTerm, matchingUserIds)) {
        rowsByOrderId.set(row.orderId, row);
      }
    }

    const rows = Array.from(rowsByOrderId.values())

    rows.sort((a, b) => {
      const aTime = a.receivedAt?.getTime() || 0;
      const bTime = b.receivedAt?.getTime() || 0;
      return bTime - aTime;
    });

    return rows;
  } catch (error) {
    logger.error('[searchAdminPayments] Search failed:', error);
    throw error;
  }
}
